import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Avatar,
  AppBar,
  Toolbar,
  useTheme,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { 
  TrendingUp,
  Flight,
  Hotel,
  CreditCard,
  AutoAwesome,
  Security,
  Speed,
  Analytics,
  CheckCircle,
  Star,
  AccountBalance,
  SwapHoriz,
  Timeline,
  LocalOffer,
  Notifications,
  Language,
  Support,
  ArrowForward,
} from '@mui/icons-material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { ThemeToggle } from '../components/common/ThemeToggle';

const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  background: theme.palette.mode === 'light'
    ? 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    : 'linear-gradient(135deg, #0c0c0c 0%, #1a1a2e 100%)',
  overflow: 'hidden',
}));

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.1); }
`;

const FloatingIcon = styled(Box)<{ delay?: number }>(({ delay = 0 }) => ({
  position: 'absolute',
  animation: `${float} 6s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  opacity: 0.1,
  pointerEvents: 'none',
}));

const GradientText = styled(Typography)(({ theme }) => ({
  background: theme.palette.mode === 'light'
    ? 'linear-gradient(135deg, #38B2AC 0%, #4FD1C7 100%)'
    : 'linear-gradient(135deg, #06b6d4 0%, #38bdf8 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: theme.palette.mode === 'light'
    ? 'rgba(255, 255, 255, 0.9)'
    : 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(20px)',
  borderRadius: 24,
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.1)'}`,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 20px 50px rgba(0, 0, 0, 0.15)'
      : '0 20px 50px rgba(0, 0, 0, 0.5)',
  },
}));

export const LandingPage: React.FC = () => {
  const theme = useTheme();
  const { signInWithGoogle, loading } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);

  const handleSignIn = async () => {
    setIsSigningIn(true);
    try {
      await signInWithGoogle();
    } catch (error: any) {
      console.error('Sign in failed:', error);
      
      // Show user-friendly error message for cross-origin issues
      if (error.message?.includes('unauthorized-domain') || 
          error.message?.includes('REDIRECT_IN_PROGRESS')) {
        console.log('🔄 Authentication redirect in progress or domain needs authorization');
        // Don't show error for redirect in progress
        if (!error.message?.includes('REDIRECT_IN_PROGRESS')) {
          alert('Authentication domain not authorized. Please check Firebase console settings or try accessing from localhost:3000');
        }
      } else {
        alert('Sign in failed. Please try again.');
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  return (
    <Box>
      {/* Navigation */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: theme.palette.mode === 'light'
            ? 'rgba(255, 255, 255, 0.8)'
            : 'rgba(15, 23, 42, 0.8)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Avatar
              sx={{
                background: 'linear-gradient(135deg, #38B2AC 0%, #4FD1C7 100%)',
                mr: 2,
                width: 40,
                height: 40,
              }}
            >
              <TrendingUp />
            </Avatar>
            <GradientText variant="h6" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
              PoinVoy
            </GradientText>
          </Box>
          
          <ThemeToggle size="medium" variant="minimal" />
          
          <Button
            variant="contained"
            onClick={handleSignIn}
            disabled={isSigningIn || loading}
            startIcon={<GoogleIcon />}
            sx={{
              ml: 2,
              background: 'linear-gradient(135deg, #4285f4 0%, #34a853 100%)',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              borderRadius: 2,
              '&:hover': {
                background: 'linear-gradient(135deg, #3367d6 0%, #2d8f47 100%)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            {isSigningIn ? 'Signing in...' : 'Get Started'}
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <HeroSection>
        {/* Floating Background Icons */}
        <FloatingIcon sx={{ top: '15%', left: '10%' }} delay={0}>
          <Flight sx={{ fontSize: 80, color: 'primary.main' }} />
        </FloatingIcon>
        <FloatingIcon sx={{ top: '25%', right: '15%' }} delay={1}>
          <Hotel sx={{ fontSize: 100, color: 'secondary.main' }} />
        </FloatingIcon>
        <FloatingIcon sx={{ bottom: '20%', left: '20%' }} delay={2}>
          <CreditCard sx={{ fontSize: 90, color: 'success.main' }} />
        </FloatingIcon>
        <FloatingIcon sx={{ bottom: '30%', right: '10%' }} delay={1.5}>
          <AutoAwesome sx={{ fontSize: 70, color: 'warning.main' }} />
        </FloatingIcon>
        
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', py: 8, position: 'relative', zIndex: 2 }}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Trust Badge */}
              <Chip
                icon={<Star sx={{ color: '#fbbf24 !important' }} />}
                label="#1 Loyalty Points Optimizer"
                sx={{
                  mb: 3,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  background: theme.palette.mode === 'light'
                    ? 'rgba(251, 191, 36, 0.1)'
                    : 'rgba(251, 191, 36, 0.2)',
                  border: '1px solid rgba(251, 191, 36, 0.3)',
                  color: '#fbbf24',
                }}
              />
              
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '5rem' },
                  fontWeight: 900,
                  mb: 3,
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                }}
              >
                Maximize Your
                <br />
                <GradientText sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                  Luxury Travel Rewards
                </GradientText>
              </Typography>

              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ 
                  mb: 4, 
                  fontWeight: 400, 
                  lineHeight: 1.7,
                  maxWidth: '850px',
                  mx: 'auto',
                  fontSize: { xs: '1.1rem', md: '1.4rem' }
                }}
              >
                The intelligent AI-powered platform that tracks, analyzes, and optimizes your 
                airline miles, hotel points, and credit card rewards across 100+ programs. 
                <strong> Never waste another point again.</strong>
              </Typography>

              {/* Stats Row */}
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 3, md: 6 }, 
                justifyContent: 'center', 
                flexWrap: 'wrap',
                mb: 6,
              }}>
                {[
                  { value: '$50M+', label: 'Points Value Tracked' },
                  { value: '100K+', label: 'Active Users' },
                  { value: '150+', label: 'Supported Programs' },
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 800, 
                          background: 'linear-gradient(135deg, #38B2AC 0%, #4FD1C7 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mb: 0.5,
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Box>

              {/* CTA Buttons */}
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSignIn}
                    disabled={isSigningIn || loading}
                    startIcon={<GoogleIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #4285f4 0%, #34a853 100%)',
                      color: 'white',
                      fontWeight: 700,
                      textTransform: 'none',
                      px: 6,
                      py: 2.5,
                      fontSize: '1.2rem',
                      borderRadius: 3,
                      boxShadow: '0 8px 30px rgba(66, 133, 244, 0.35)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #3367d6 0%, #2d8f47 100%)',
                        boxShadow: '0 12px 40px rgba(66, 133, 244, 0.45)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {isSigningIn ? 'Signing in...' : 'Start Free Today'}
                  </Button>
                </motion.div>
              </Box>

              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: 2, 
                mt: 4,
                flexWrap: 'wrap'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Star sx={{ color: '#fbbf24', fontSize: 20 }} />
                  <Star sx={{ color: '#fbbf24', fontSize: 20 }} />
                  <Star sx={{ color: '#fbbf24', fontSize: 20 }} />
                  <Star sx={{ color: '#fbbf24', fontSize: 20 }} />
                  <Star sx={{ color: '#fbbf24', fontSize: 20 }} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Rated 4.9/5 by 10,000+ travelers
                </Typography>
                <Chip 
                  label="No credit card required" 
                  size="small" 
                  sx={{ 
                    bgcolor: theme.palette.mode === 'light' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.2)',
                    color: '#10b981',
                    fontWeight: 600,
                  }} 
                />
              </Box>
            </motion.div>
          </Box>
        </Container>
      </HeroSection>

      {/* Features Section */}
      <Box sx={{ 
        py: 12, 
        background: theme.palette.mode === 'light'
          ? 'rgba(255, 255, 255, 0.8)'
          : 'rgba(18, 18, 18, 0.8)',
      }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}
              >
                Everything You Need in{' '}
                <GradientText sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                  One Platform
                </GradientText>
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ maxWidth: '700px', mx: 'auto', fontWeight: 400 }}
              >
                Powerful features designed to maximize the value of every point, mile, and reward you earn
              </Typography>
            </Box>

            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, 
              gap: 4,
            }}>
              {[
                {
                  icon: <Flight />,
                  title: 'Multi-Program Tracking',
                  description: 'Track airline miles across 100+ programs including American, Delta, United, Emirates, and more. Real-time balance updates and expiry alerts.',
                  gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                },
                {
                  icon: <Hotel />,
                  title: 'Hotel Points Manager',
                  description: 'Manage points from Marriott Bonvoy, Hilton Honors, IHG, Hyatt, and all major hotel chains in one centralized dashboard.',
                  gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                },
                {
                  icon: <CreditCard />,
                  title: 'Credit Card Optimizer',
                  description: 'Optimize your spending across multiple credit cards. Get recommendations on which card to use for maximum rewards on every purchase.',
                  gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                },
                {
                  icon: <AutoAwesome />,
                  title: 'AI-Powered Insights',
                  description: 'Advanced machine learning algorithms analyze your portfolio and suggest optimal transfer partners, redemption opportunities, and earning strategies.',
                  gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                },
                {
                  icon: <SwapHoriz />,
                  title: 'Smart Transfers',
                  description: 'Calculate transfer ratios in real-time. Know exactly how many miles you\'ll get when transferring between programs before you commit.',
                  gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
                },
                {
                  icon: <Timeline />,
                  title: 'Portfolio Analytics',
                  description: 'Detailed charts and insights showing your points growth, redemption history, and portfolio value over time with export capabilities.',
                  gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <FeatureCard>
                    <CardContent sx={{ p: 4 }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 3,
                          background: feature.gradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3,
                          '& svg': {
                            fontSize: 32,
                            color: 'white',
                          }
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </motion.div>
              ))}
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ 
        py: 12, 
        background: theme.palette.mode === 'light'
          ? 'rgba(56, 178, 172, 0.05)'
          : 'rgba(56, 178, 172, 0.1)',
      }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h2"
              align="center"
              sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}
            >
              Why Travelers Love{' '}
              <GradientText sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                PoinVoy
              </GradientText>
            </Typography>
            <Typography 
              variant="h6" 
              align="center"
              color="text.secondary" 
              sx={{ mb: 8, maxWidth: '700px', mx: 'auto', fontWeight: 400 }}
            >
              Join thousands of smart travelers maximizing their rewards every day
            </Typography>

            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
              gap: 3,
            }}>
              {[
                { icon: <CheckCircle />, text: 'Track unlimited loyalty programs across airlines, hotels, and credit cards' },
                { icon: <CheckCircle />, text: 'Real-time balance updates and expiry date monitoring' },
                { icon: <CheckCircle />, text: 'AI-powered recommendations for optimal point transfers and redemptions' },
                { icon: <CheckCircle />, text: 'Calculate best redemption value across all your programs instantly' },
                { icon: <CheckCircle />, text: 'Get notified of devaluations, bonuses, and special promotions' },
                { icon: <CheckCircle />, text: 'Compare award availability and find hidden sweet spots' },
                { icon: <CheckCircle />, text: 'Detailed portfolio analytics with charts and historical tracking' },
                { icon: <CheckCircle />, text: 'Credit card spending optimizer for maximum rewards per dollar' },
                { icon: <CheckCircle />, text: 'Bank-level encryption and enterprise security standards' },
                { icon: <CheckCircle />, text: 'Access from any device - desktop, tablet, or mobile' },
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        mt: 0.5,
                        '& svg': {
                          fontSize: 18,
                          color: 'white',
                        }
                      }}
                    >
                      {benefit.icon}
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.7 }}>
                      {benefit.text}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 12,
          background: theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, #38B2AC 0%, #4FD1C7 100%)'
            : 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated Background Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            animation: `${pulse} 4s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '15%',
            right: '10%',
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            animation: `${pulse} 5s ease-in-out infinite`,
            animationDelay: '1s',
          }}
        />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h2"
                sx={{ 
                  fontWeight: 900, 
                  mb: 3, 
                  color: 'white',
                  fontSize: { xs: '2rem', md: '3.5rem' },
                  lineHeight: 1.2,
                }}
              >
                Ready to Unlock Maximum Value from Your Points?
              </Typography>
              <Typography
                variant="h5"
                sx={{ 
                  mb: 5, 
                  opacity: 0.95, 
                  maxWidth: '700px', 
                  mx: 'auto',
                  fontWeight: 400,
                  lineHeight: 1.6,
                }}
              >
                Join over 100,000 smart travelers who are already maximizing their loyalty programs. 
                Start your free account today and never waste another point.
              </Typography>
              
              {/* Feature Pills */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'center', 
                flexWrap: 'wrap',
                mb: 5,
              }}>
                {[
                  'Free Forever',
                  'No Credit Card',
                  'Cancel Anytime',
                  '5-Minute Setup'
                ].map((pill, index) => (
                  <Chip
                    key={index}
                    label={pill}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}
                  />
                ))}
              </Box>
              
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSignIn}
                  disabled={isSigningIn || loading}
                  startIcon={<GoogleIcon />}
                  sx={{
                    background: 'white',
                    color: '#38B2AC',
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 8,
                    py: 3,
                    fontSize: '1.3rem',
                    borderRadius: 3,
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                    '&:hover': {
                      background: '#f8f9fa',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 15px 50px rgba(0, 0, 0, 0.35)',
                    },
                  }}
                >
                  {isSigningIn ? 'Signing in...' : 'Get Started Free - No Credit Card'}
                </Button>
              </motion.div>
              
              <Typography 
                variant="body2" 
                sx={{ mt: 4, opacity: 0.9, fontWeight: 500 }}
              >
                ✨ Instant access • ✓ 100% Free • 🔒 Secure & Private
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          py: 4,
          background: theme.palette.mode === 'light' ? '#f8f9fa' : '#0a0a0a',
          borderTop: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  background: 'linear-gradient(135deg, #38B2AC 0%, #4FD1C7 100%)',
                  width: 36,
                  height: 36,
                }}
              >
                <TrendingUp />
              </Avatar>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                © 2024 PoinVoy. Maximizing rewards, one point at a time.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>
                Privacy
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>
                Terms
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>
                Contact
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};